import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { askKb } from '@/api/kb';
import type { KbAnswerOut } from '@/api/types';
import { KB_USAGE_ROLLUP_KEY, KbCostWidget } from '@/components/KbCostWidget';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { citationLabel, formatUsdCost } from '@/lib/kb-cost';

import { useAuth } from '../auth/AuthContext';

export function AskKbPage() {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState('');

  const ask = useMutation({
    mutationFn: async (q: string): Promise<KbAnswerOut> =>
      askKb(q, (await getIdToken()) ?? ''),
    // A KB call bills into kb_usage — refresh the cost widget. Explicit
    // invalidation (not a key derived from the answer) so a repeated question
    // returning an identical answer still triggers a refetch.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KB_USAGE_ROLLUP_KEY }),
  });

  const answer = ask.data;
  const canAsk = question.trim().length > 0 && !ask.isPending;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-brand-navy">Ask the knowledge base</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Free-form GST/HST questions answered from the CRA corpus. Every claim is cited;
            the assistant abstains rather than guess. Read-only — nothing here changes a finding.
          </p>
        </div>
        <KbCostWidget />
      </header>

      <Card>
        <CardContent className="pt-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (canAsk) ask.mutate(question.trim());
            }}
            className="flex flex-col gap-3"
          >
            <label htmlFor="kb-question" className="sr-only">
              Your GST/HST question
            </label>
            <textarea
              id="kb-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Can we claim an ITC on an employee expense reimbursement?"
              rows={3}
              maxLength={2000}
              className="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-royal"
            />
            <div className="flex items-center justify-end">
              <Button type="submit" disabled={!canAsk}>
                {ask.isPending ? (
                  <>
                    <Spinner /> Asking…
                  </>
                ) : (
                  'Ask'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {ask.isError ? (
        <Card>
          <CardContent className="pt-6 text-sm text-danger">
            Something went wrong asking the knowledge base. Please try again.
          </CardContent>
        </Card>
      ) : null}

      {answer ? <AnswerCard answer={answer} /> : null}
    </div>
  );
}

function AnswerCard({ answer }: { answer: KbAnswerOut }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {answer.grounded ? 'Answer' : 'No grounded answer'}
        </CardTitle>
        <CardDescription>
          {answer.grounded
            ? `${answer.model} · actual ${formatUsdCost(Number(answer.cost_usd))} · est. ${formatUsdCost(Number(answer.estimated_cost_usd))}`
            : 'The corpus did not contain enough to answer without guessing.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="whitespace-pre-wrap text-sm text-foreground">{answer.text}</p>

        {answer.citations.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Sources
            </div>
            <ul className="flex flex-col gap-2">
              {answer.citations.map((c, i) => (
                <li key={`${c.doc_code}-${c.para_range}-${i}`} className="text-sm">
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-royal hover:underline"
                  >
                    {citationLabel(c.doc_code, c.para_range)}
                  </a>
                  {c.title ? <span className="text-muted-foreground"> — {c.title}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
